class FormatterExtensions {
  // Returns a number formatter for the given decimal places
  static getNumberFormat(decimals) {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }

  // Returns a currency formatter for the given decimal places and currency symbol
  static getCurrencyFormat(decimals, currency) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
      currencyDisplay: 'symbol',
    });
  }

  // Returns a percent formatter for the given decimal places
  static getPercentFormat(decimals) {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }

  /**
   * Formats a numeric value with specified decimal places.
   *
   * @param {number|null|undefined} value - The value to format.
   * @param {Object} [options] - Optional settings.
   * @param {number} [options.decimals=2] - Number of decimal places.
   * @param {string} [options.default='-'] - Fallback value for null/invalid input.
   * @returns {string}
   */
  static num(value, { decimals = 2, default: d = '-' } = {}) {
    if (value === null || value === undefined || isNaN(value)) return d;
    return FormatterExtensions.getNumberFormat(decimals).format(value);
  }

  /**
   * Formats a numeric value as currency.
   *
   * @param {number|null|undefined} value - The value to format.
   * @param {Object} [options] - Optional settings.
   * @param {number} [options.decimals=2] - Number of decimal places.
   * @param {string} [options.currency='$'] - Currency code.
   * @param {string} [options.default='-'] - Fallback value for null/invalid input.
   * @returns {string}
   */
  static curr(value, { decimals = 2, currency = 'USD', default: d = '-' } = {}) {
    if (value === null || value === undefined || isNaN(value)) return d;
    return FormatterExtensions.getCurrencyFormat(decimals, currency).format(
      value
    );
  }

  /**
   * Formats a fractional value as a percentage.
   * Note: Input must be in fractional form (e.g., 0.25 for 25%)
   *
   * @param {number|null|undefined} value - The fractional value to format.
   * @param {Object} [options] - Optional settings.
   * @param {number} [options.decimals=2] - Number of decimal places.
   * @param {string} [options.default='-'] - Fallback value for null/invalid input.
   * @param {string} [options.symbol='%'] - Symbol to append (e.g., %, ‱).
   * @returns {string}
   */
  static pct(value, { decimals = 2, default: d = '-', symbol = '%' } = {}) {
    if (value === null || value === undefined || isNaN(value)) return d;
    return (
      FormatterExtensions.num(value * 100, { decimals, default: d }) + symbol
    );
  }
}

export { FormatterExtensions }
